/**
 * BlueHat widget IIFE snippet.
 * Customers embed this via <script src="https://abc.bluehatchat.test/bh.js"></script>.
 * It creates a chat bubble (shadow DOM) and a hidden iframe for
 * service worker communication with the backend.
 *
 * Mount modes:
 * - Floating (default): chat bubble fixed bottom-right.
 * - Inline: if a <div class="bhai-<customerId>-chat"></div> exists on
 *   the page, the chat mounts inside it (always-open, no bubble).
 *   Detection runs immediately + via a persistent MutationObserver so
 *   React-rendered targets are picked up after the fact.
 */

import { createBubble, type BubbleHandle } from "./bubble.tsx";
import { setNudgePool, clearNudgePool, nudgePool, currentNudgeView, rotateNudges, markNudgeAsked, setNudgeFloatCount, setNudgeMobileCount } from "./nudge-pool.ts";
import { startNudgeScroll, type NudgeScrollHandle } from "./nudge-scroll.ts";
import { debugLog } from "./debug.ts";
import { extractCtaArrival, productIdFromSlug, stripCtaParam } from "@bluehat/shared/events/cta-attribution";
import { setupObservers } from "./observers/index.ts";
import { isAdminMode, injectAdminStyles, markTriggered } from "./admin.ts";
import { executeCommand, drainActionStack } from "./actions.ts";
import { renderPlacements, applyTheme } from "./placements.ts";
import { setScopeRules, setMasterOff, isMasterOff, setShowOnPhones, isPhoneHidden, evaluateScopeForCurrentUrl, currentGates } from "./scope.ts";
import { PHONE_BREAKPOINT_PX } from "@bluehat/shared/widget-design/inline";
import type { ParentMessage, IframeMessage, Trigger, EventMessage, BannerConfig } from "./messages.ts";
import type { WidgetRuntimeDesign } from "@bluehat/shared/widget-design/runtime";
import {
  DEFAULT_LIVING_BAR_SKIN,
  isLivingBarSkin,
  type LivingBarSkin,
} from "@bluehat/shared/widget-design/living-bar";
import {
  DEFAULT_INLINE_MODE,
  inlineChipsAllowed,
  inlineModeFor,
  isBarMode,
  lowerBarAllowed,
  DEFAULT_BAR_MODE,
  type BarMode,
  isInlineMode,
  isBarContent,
  DEFAULT_BAR_CONTENT,
  type BarContent,
  type InlineMode,
} from "@bluehat/shared/widget-design/inline";
import { resolveVoiceMode, type VoiceMode } from "@bluehat/shared/voice/mode";
import { IN_PAGE_BAR_KEYS } from "@bluehat/shared/widget-design/in-page-bar";
import { buildBarQueue } from "./bar-queue.ts";
import {
  clearAskedPageQuestions,
  filterUnaskedPageQuestions,
  markPageQuestionAsked,
} from "./page-pills.ts";
import { setupScrollDock, type ScrollDockHandle } from "./scroll-dock.ts";
import { createTypewriter } from "./typewriter.ts";
import {
  buildSectionBarView,
  type PageSection,
  type SectionBarView,
} from "./section-voice.ts";
import { startSectionScroll, type SectionScrollHandle } from "./section-scroll.ts";
import {
  setTtsApiOrigin,
  setTtsVoice,
  warmOpenAiTts,
} from "./voice-talk.ts";
import { setSttEngine, setSttAssetOrigin } from "./stt/adapter.ts";
import {
  clearConversationFocus,
  readConversationFocus,
} from "./conversation-focus.ts";
import { resolveTextLandPresentation } from "./text-continuity.ts";
import { pageTopic } from "./page-topic.ts";
import { historyAnswersAsk, PENDING_ID_PREFIX, placeholderIdFor } from "./pending-answer.ts";
import { clearPathJourney } from "./path-journey.ts";
import { createExposureReporter, NO_AB, resolveAbTests, type AbState } from "./ab-tests.ts";
import { EXPERIMENT_EXPOSURE_EVENT } from "@bluehat/shared/experiments/experiments";


declare global {
  interface Window {
    /** Set by the first bh.js execution — makes a second inclusion a no-op. */
    __bhaiLoaded?: boolean;
    /**
     * Dev/QA: wipe host session + asked pills + focus and reload.
     * Prefer: open `?bh_new_session=1` on the page URL.
     */
    __bhaiNewSession?: () => void;
  }
}

const HOST_SESSION_KEY = "bh_sid";

/** Host-page keys the widget writes (session + local). Cleared on new session. */
const HOST_WIDGET_STORAGE_PREFIXES = ["bh_", "bhai_", "bhai-"] as const;

/**
 * True when the embedding page asked for a clean conversation.
 * One-shot: strips the query flag so a later refresh doesn't keep wiping.
 */
function consumeNewSessionFlag(): boolean {
  try {
    const u = new URL(window.location.href);
    const flag =
      u.searchParams.has("bh_new_session") ||
      u.searchParams.has("bh_reset") ||
      u.searchParams.get("bh_session") === "new";
    if (!flag) return false;
    u.searchParams.delete("bh_new_session");
    u.searchParams.delete("bh_reset");
    if (u.searchParams.get("bh_session") === "new") u.searchParams.delete("bh_session");
    const q = u.searchParams.toString();
    const next = u.pathname + (q ? `?${q}` : "") + u.hash;
    window.history.replaceState(null, "", next);
    return true;
  } catch {
    return false;
  }
}

function clearHostWidgetStorage(): void {
  try {
    localStorage.removeItem(HOST_SESSION_KEY);
  } catch {
    /* private mode */
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k) continue;
      if (HOST_WIDGET_STORAGE_PREFIXES.some((p) => k.startsWith(p))) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* private mode */
  }
  try {
    clearAskedPageQuestions();
  } catch {
    /* ignore */
  }
  try {
    clearConversationFocus();
  } catch {
    /* ignore */
  }
  try {
    clearPathJourney();
  } catch {
    /* ignore */
  }
}

(function () {
  const scriptEl = document.currentScript as HTMLScriptElement | null;
  if (!scriptEl) return;

  // Guard against double-injection: if bh.js is included twice on a page (a
  // template + a tag manager, a duplicate embed, etc.), a second execution would
  // spin up a second iframe + WebSocket + bubble — and the server fans messages
  // out to EVERY socket in a session, so the whole conversation renders twice.
  // First run wins; the flag lives on window (shared across both IIFE runs).
  if (window.__bhaiLoaded) return;
  window.__bhaiLoaded = true;

  // ── Force clean conversation (dev / QA) ────────────────────────────────
  // Usage: http://localhost:3000/?bh_new_session=1  or  ?bh_reset=1
  // Also: window.__bhaiNewSession() then hard reload.
  // Keep forceNewSession sticky until a *fresh* sid is minted (not the wiped one).
  let forceNewSession = consumeNewSessionFlag();
  let wipedHostSid: string | null = null;
  if (forceNewSession) {
    try {
      wipedHostSid = localStorage.getItem(HOST_SESSION_KEY);
    } catch {
      wipedHostSid = null;
    }
    clearHostWidgetStorage();
    console.info(
      "%c[BlueHat] NEW SESSION",
      "background:#0b5;color:#fff;padding:2px 6px;border-radius:3px",
      "— cleared host bh_sid" +
        (wipedHostSid ? ` (was ${wipedHostSid.slice(0, 8)}…)` : "") +
        ", asked pills, focus. Iframe will remint. Empty chat = success.",
    );
  }
  window.__bhaiNewSession = () => {
    try {
      wipedHostSid = localStorage.getItem(HOST_SESSION_KEY);
    } catch {
      /* ignore */
    }
    clearHostWidgetStorage();
    // Extra hard clear of any lingering host widget keys (asked pills, voice, etc.).
    try {
      const drop: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (HOST_WIDGET_STORAGE_PREFIXES.some((p) => k.startsWith(p))) drop.push(k);
      }
      for (const k of drop) localStorage.removeItem(k);
    } catch {
      /* private mode */
    }
    const u = new URL(window.location.href);
    u.searchParams.set("bh_new_session", "1");
    // Cache-bust so bh.js / iframe is not an old bundle without wipe remint.
    u.searchParams.set("_bht", String(Date.now()));
    console.info(
      "%c[BlueHat] NEW SESSION reload",
      "background:#0b5;color:#fff;padding:2px 6px;border-radius:3px",
      "→ ?bh_new_session=1",
    );
    window.location.replace(u.toString());
  };

  const scriptUrl = new URL(scriptEl.src);
  const origin = scriptUrl.origin;
  // OpenAI TTS for click-to-talk (same tenant origin as bh.js / widget-api).
  setTtsApiOrigin(origin);
  setSttAssetOrigin(origin);
  // Best-effort warm so first Click-to-talk isn't a cold OpenAI round-trip.
  warmOpenAiTts();

  // Customer id = first subdomain label (e.g. qe8jtsy4.bluehatchat.test → qe8jtsy4).
  // Treat bare hostnames / IPs / localhost as no-customer → floating only.
  const firstLabel = scriptUrl.hostname.split(".")[0];
  const customerId =
    firstLabel && firstLabel !== scriptUrl.hostname && /^[a-z0-9][a-z0-9-]*$/i.test(firstLabel)
      ? firstLabel
      : null;
  const inlineSelector = customerId ? `div.bhai-${CSS.escape(customerId)}-chat` : null;
  // Per-page banner switch: a div.bhai-<id>-banner on a page opts that page
  // into the top banner (mirrors the inline-chat container). Its data-*
  // attributes override the dashboard-saved fallback banner.
  const bannerSelector = customerId ? `div.bhai-${CSS.escape(customerId)}-banner` : null;

  const admin = isAdminMode();
  let observerCleanup: (() => void) | null = null;
  let adminStyleCleanup: (() => void) | null = null;
  let activeTriggers: Trigger[] | null = null;

  // Hidden iframe for SW communication. The iframe self-detects
  // trial-mode (vs live) from document.referrer — when its parent
  // is a dashboard host (<id>.dashboard.<base>), it switches to
  // preview mode and stamps that on its API calls. See iframe.ts.
  const iframe = document.createElement("iframe");
  // Pass admin status into the iframe at load so it can gate SW cache-busting
  // at registration time. The iframe is a different origin and can't read the
  // parent page's bh_admin flag, and the admin:enable message arrives AFTER
  // register() runs — so the URL param is the only way to know in time.
  // Carry the debug flag ACROSS THE FRAME BOUNDARY. debugLog reads
  // location.search and localStorage, both of which are the IFRAME's — a
  // different origin from the host page. So `?bh_debug=1` on the customer's
  // site turned on the snippet's logging and left the iframe silent, which is
  // where the preload, the session, the WebSocket and the kill switch all
  // live. Every diagnostic in there was unreachable from the page you debug
  // it on.
  const wantDebug = /[?&]bh_debug=1\b/.test(location.search);
  const iframeParams = [admin ? "admin=1" : "", wantDebug ? "bh_debug=1" : ""].filter(Boolean).join("&");
  iframe.src = iframeParams ? `${origin}/iframe.html?${iframeParams}` : `${origin}/iframe.html`;
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
  // Append when <body> exists. The snippet is an IIFE that runs the moment
  // bh.js loads — a customer who puts the tag in <head> (or before <body>
  // parses) has no document.body yet, and an unguarded appendChild threw
  // "Cannot read properties of null" and killed the ENTIRE widget IIFE
  // (mount, SW handshake, route watching — all of it). The element itself
  // is created above so the closures below can reference it immediately;
  // only its insertion waits for the DOM.
  function mountIframe() {
    document.body.appendChild(iframe);
  }
  if (document.body) {
    mountIframe();
  } else {
    document.addEventListener("DOMContentLoaded", mountIframe, { once: true });
  }

  // Buffer messages until the iframe has loaded — inline mode mounts on
  // DOMContentLoaded and fires `chat:open` immediately, which can beat the
  // iframe's load event and get dropped before its message listener exists.
  let iframeReady = false;
  const pendingToIframe: ParentMessage[] = [];

  /**
   * Behavioural telemetry switch (tenant setting). When on, the parent emits no
   * events at all — see the guard in postToIframe and the observer skip in
   * handleTriggers.
   *
   * It exists because `terminated` deliberately does NOT stop tracking: killing
   * the widget tears down the surfaces and keeps the event stream running, so a
   * tenant who thought they had switched everything off kept accruing pageviews,
   * dwell, scroll, click and form events. A form event is what mints a
   * conversion, which is how a store with a disabled widget still reported
   * conversions (Stag Shop, 2026-09-24).
   */
  let telemetryOff = false;

  function postToIframe(msg: ParentMessage) {
    // FIRST, so a queued event cannot flush later either: telemetry off means
    // no behavioural event ever leaves the page. Chat, config and control
    // messages are untouched — the iframe is still the only channel that can
    // deliver an un-terminate or carry a conversation.
    if (telemetryOff && msg.type === "event") return;
    if (!iframeReady) {
      pendingToIframe.push(msg);
      return;
    }
    iframe.contentWindow?.postMessage(msg, origin);
  }

  // #487: the engaged "Next for you" rail override is a PANEL-OPEN state.
  // It used to latch forever once a conversation existed, so after closing
  // the chat the rail repainted the same ranked 3 on every scroll ("pills
  // freeze until refresh"). Closing the panel now hands the rail straight
  // back to scroll-following sections.
  let panelOpenForRail = false;

  const callbacks = {
    onOpen() {
      panelOpenForRail = true;
      postToIframe({ type: "chat:open" });
      pushSectionBarView();
    },
    onClose() {
      panelOpenForRail = false;
      postToIframe({ type: "chat:close" });
      // Immediate un-freeze — don't wait for the next scroll event.
      pushSectionBarView();
    },
    onSend(text: string) {
      // Any send (pill, typed, placement) — session-wide so pills never recycle.
      markPageQuestionAsked(text);
      // Drop the asked question from under-bar corpus chips + typewriter queue.
      reapplyLivingBarFromCache();
      postToIframe({ type: "chat:send", text, url: window.location.href });
    },
    onSubmitLead(values: Record<string, string>) {
      // Prefer chat path so rich-lead handoff can extract name/email/phone and
      // continue the lane (lead:submit alone stores a lead but skips the brain).
      const parts: string[] = [];
      const name = values.name?.trim();
      const email = values.email?.trim();
      const phone = values.phone?.trim();
      const website = (values.website ?? values.site ?? values.url)?.trim();
      if (name) parts.push(`My name is ${name}`);
      if (email) parts.push(`my email is ${email}`);
      if (phone) parts.push(`my phone is ${phone}`);
      if (website) parts.push(`my website is ${website}`);
      for (const [k, v] of Object.entries(values)) {
        if (k === "name" || k === "email" || k === "phone" || k === "website" || k === "site" || k === "url") {
          continue;
        }
        const t = (v ?? "").trim();
        if (t) parts.push(`${k}: ${t}`);
      }
      const text = parts.join(". ");
      if (text) {
        postToIframe({ type: "chat:send", text, url: window.location.href });
      } else {
        postToIframe({ type: "lead:submit", values, url: window.location.href });
      }
    },
    // Real CTA click-through (#156): record it, then navigate. The event is
    // relayed to the bridge iframe (posted with keepalive so it survives this
    // page's unload); a short defer lets the relay reach the iframe before we
    // navigate away. `url` is the CTA destination that was clicked.
    // Rich-lead offer chip accepted: relay the opt-in event to the bridge
    // iframe (the accept text itself flows through onSend above).
    onChipClick(_sendText: string) {
      postToIframe({ type: "event", event: "rich_lead_opt_in", url: window.location.href });
    },
    // Answer-choice chips (ai.routing.answerChips) — measure lift; NEVER opt-in.
    onAnswerChipsShown(
      labels: string[],
      turnId: string,
      chipKind?: "answer" | "context_next_ask",
    ) {
      postToIframe({
        type: "event",
        event: "answer_chips_shown",
        labels,
        turnId,
        url: window.location.href,
        ...(chipKind ? { chip_kind: chipKind } : {}),
      } as EventMessage);
    },
    onAnswerChipClick(label: string, turnId: string) {
      postToIframe({
        type: "event",
        event: "answer_chip_clicked",
        label,
        turnId,
        url: window.location.href,
      } as EventMessage);
    },
    onPageQuestionPillsShown(questionHashes: string[], source: "bar_submit" | "bar_rest") {
      postToIframe({
        type: "event",
        event: "page_question_pills_shown",
        questionHashes,
        source,
        url: window.location.href,
      } as EventMessage);
    },
    onPageQuestionPillClicked(
      questionHash: string,
      question: string,
      source: "bar_submit" | "bar_rest",
    ) {
      // Asked nudges never resurface this session; repaint drops it now.
      markNudgeAsked(question);
      pushSectionBarView();
      postToIframe({
        type: "event",
        event: "page_question_pill_clicked",
        questionHash,
        question,
        source,
        url: window.location.href,
      } as EventMessage);
    },
    onClickProduct(productId: string, url: string) {
      if (!url || !productId) return;
      // The pre-navigation credit. Its counterpart is the arrival credit in
      // checkCtaArrival, which exists because this POST races the unload — if the
      // page goes away first, the stamped `bhai_ctaslug=p_<id>` on the destination
      // still gets the click counted. Dedup per (session, product) collapses the
      // double when both land.
      postToIframe({
        type: "event",
        event: "product_click",
        productId,
        url,
        via: "click",
      });
      // Same tab, same reasoning as onClickCta below: never window.open.
      window.setTimeout(() => {
        try {
          const abs = new URL(url, window.location.href);
          if (abs.origin === window.location.origin) {
            window.location.assign(abs.pathname + abs.search + abs.hash);
          } else {
            window.location.assign(abs.href);
          }
        } catch {
          window.location.assign(url);
        }
      }, 150);
    },
    onClickCta(url: string, slug: string, _openInNewTab: boolean, source?: "presented" | "panel_header") {
      if (!url) return;
      if (slug) {
        postToIframe({
          type: "event",
          event: "cta_click",
          ctaSlug: slug,
          url,
          via: "click",
          ...(source ? { source } : {}),
        });
      }
      // ALWAYS same tab — voice, click, in-site path, or Calendly. Never
      // window.open / target=_blank (popup blockers + navigational CTAs).
      // openInNewTab from dashboard config is ignored for execution.
      window.setTimeout(() => {
        try {
          const abs = new URL(url, window.location.href);
          // Same origin: keep path so in-site CTAs feel like normal navigation.
          if (abs.origin === window.location.origin) {
            window.location.assign(abs.pathname + abs.search + abs.hash);
          } else {
            window.location.assign(abs.href);
          }
        } catch {
          window.location.href = url;
        }
      }, 150);
    },
    onBarEvent(ev: {
      event: string;
      kind?: string;
      submitted?: boolean;
      reason?: string;
      said?: string;
      pill?: string | null;
      rankSource?: string;
      matchesFocus?: boolean;
      navIndex?: number;
      includeMeta?: boolean;
      outcome?: string;
      // voice_intent host-line request fields (pill already declared above).
      trigger?: string;
      sectionIndex?: number;
      panelOpen?: boolean;
      transcript?: string;
      intentId?: string;
      // surface_pill_clicked
      skin?: string;
      question?: string;
    }) {
      // Surface pill taps (capsule/notes/rails) mark the nudge asked-set —
      // same rule as the panel pills; repaint drops the question now.
      if (ev.event === "surface_pill_clicked" && ev.question) {
        markNudgeAsked(ev.question);
        pushSectionBarView();
      }
      postToIframe({
        type: "event",
        event: ev.event,
        url: window.location.href,
        ...(ev.kind != null ? { kind: ev.kind } : {}),
        ...(ev.submitted != null ? { submitted: ev.submitted } : {}),
        ...(ev.reason != null ? { reason: ev.reason } : {}),
        ...(ev.said != null ? { said: ev.said } : {}),
        ...(ev.pill != null ? { pill: ev.pill } : {}),
        ...(ev.rankSource != null ? { rankSource: ev.rankSource } : {}),
        ...(ev.matchesFocus != null ? { matchesFocus: ev.matchesFocus } : {}),
        ...(ev.navIndex != null ? { navIndex: ev.navIndex } : {}),
        ...(ev.includeMeta != null ? { includeMeta: ev.includeMeta } : {}),
        ...(ev.outcome != null ? { outcome: ev.outcome } : {}),
        ...(ev.trigger != null ? { trigger: ev.trigger } : {}),
        ...(ev.sectionIndex != null ? { sectionIndex: ev.sectionIndex } : {}),
        ...(ev.panelOpen != null ? { panelOpen: ev.panelOpen } : {}),
        ...(ev.transcript != null ? { transcript: ev.transcript } : {}),
        ...(ev.intentId != null ? { intentId: ev.intentId } : {}),
        ...(ev.skin != null ? { skin: ev.skin } : {}),
        ...(ev.question != null ? { question: ev.question } : {}),
      } as EventMessage);
    },
  };

  let scrollDock: ScrollDockHandle | null = null;

  let lastHeroMonologue: string[] | undefined;
  /** Full page-voice corpus for this URL (before session-asked filter). */
  let lastPageQuestions: string[] = [];
  /** Heading-grouped sections for scroll-aware dual chrome. */
  let lastPageSections: PageSection[] = [];
  /** True once a pagevoice:update payload has landed (even an empty one). */
  let pageVoiceLanded = false;
  /**
   * Tenant voice mode — THE single voice truth on this side. `lastVoiceAssist`
   * is DERIVED from it (mode !== "off"), never set independently: the P0 this
   * replaced came precisely from voiceAssist and voiceMode being two writable
   * copies of one fact with only one of them wired to the per-URL scope gate.
   */
  let lastVoiceMode: VoiceMode = "off";
  let lastVoiceAssist = false;
  /**
   * Tenant mode gated by the CURRENT page's scope verdict — the ONLY form any
   * surface may consume. A denied page is mode "off", which is what makes
   * every downstream flag (voiceAssist, classicVoice) false at once.
   */
  function pageVoiceMode(): VoiceMode {
    return currentGates().voice ? lastVoiceMode : "off";
  }
  let lastSectionIndex = 0;
  let sectionScroll: SectionScrollHandle | null = null;
  /**
   * Debounce scroll-driven voice announces so fast section thrash does not
   * hard-abort in-flight TTS/mic on every intermediate land. Bar UI still
   * updates immediately via pushSectionBarView.
   */
  let voiceNavAnnounceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * After section-scroll settles, wait this long before requesting a voice
   * announce. Longer = less thrash; speech itself never cuts mid-sentence
   * (bubble queues while TTS/listen busy).
   */
  let lastInviteLines: string[] | undefined;
  /** Last typewriter queue texts for in-page placements — re-applied after every
   *  renderPlacements (which rebuilds .bhai-tw-target nodes). */
  let lastPlacementTwTexts: string[] = [];

  /**
   * Unasked Qs from the full page pool (session-filtered).
   * Display window is 3; typewriter can use a few more of the unasked set.
   */
  function unaskedPageQuestions(max = 12): string[] {
    return filterUnaskedPageQuestions(lastPageQuestions, max);
  }

  function currentSectionView(): SectionBarView {
    // Only overlay "Next for you" when the visitor has actually chatted
    // (conversation focus). Cold server-bar rank must NOT shrink the rail
    // from full section dual-chrome (3+ coming-up) down to 2 cards.
    const focus = readConversationFocus();
    const engaged =
      panelOpenForRail &&
      !!focus?.lastUser?.trim() &&
      lastRankedPagePills &&
      lastRankedPagePills.length > 0;
    return buildSectionBarView(lastPageSections, lastSectionIndex, {
      engagedRanked: engaged ? lastRankedPagePills! : undefined,
    });
  }

  /** Remove the retired floating section-stage host if an older session's
   *  bundle left it in the DOM (the module is deleted — pills rewrite P6). */
  function clearRetiredSectionStage() {
    try {
      document.getElementById("bhai-ai-section-stage")?.remove();
    } catch {
      /* ignore */
    }
  }

  function pushSectionBarView(idx?: number) {
    if (typeof idx === "number") lastSectionIndex = idx;
    // Nudge pool (pills rewrite P4): when the page HAS verified nudge
    // questions, they are the only source — the section-matched view below
    // is the legacy path and dies with the demolition commit.
    if (nudgePool().length > 0) {
      const nudgeView = currentNudgeView();
      handle?.setSectionBarView?.(nudgeView.active ? nudgeView : null);
      clearRetiredSectionStage();
      return;
    }
    // Engaged rail can render with ranked pills even before page sections load.
    const view = currentSectionView();
    if (!view.active && lastPageSections.length === 0) {
      handle?.setSectionBarView?.(null);
      clearRetiredSectionStage();
      return;
    }
    // Sticky living bar — section dual-chrome skins (dashboard-chosen).
    // When engagedRanked is set, view uses "Next for you" + ranked inventory.
    handle?.setSectionBarView?.(view.active ? view : null);
    clearRetiredSectionStage();
  }

  function clearVoiceNavAnnounceTimer() {
    if (voiceNavAnnounceTimer != null) {
      clearTimeout(voiceNavAnnounceTimer);
      voiceNavAnnounceTimer = null;
    }
  }

  function restartSectionScroll() {
    sectionScroll?.stop();
    sectionScroll = null;
    clearVoiceNavAnnounceTimer();
    // -1 so the first onChange(0) is never dropped as a no-op.
    lastSectionIndex = -1;
    if (lastPageSections.length === 0) {
      handle?.setSectionBarView?.(null);
      clearRetiredSectionStage();
      return;
    }
    sectionScroll = startSectionScroll({
      sections: lastPageSections,
      // Snappier than default so bar chrome tracks reading position sooner.
      settleMs: 220,
      onChange: (idx) => {
        if (idx === lastSectionIndex) {
          // Same index can still need a re-push after mount race / session-asked.
          pushSectionBarView(idx);
          return;
        }
        // Nudge rotation (pills rewrite P4): a settled scroll into a new band
        // deals the next window — the "page is watching" feel, zero network.
        rotateNudges();
        pushSectionBarView(idx);
        // Server-side section awareness: report the settled section so host-line
        // assembly + the living bar can read it back from session_events. The
        // server has the sections; only the browser knows the scroll position.
        postToIframe({
          type: "event",
          event: "section_view",
          sectionIndex: idx,
          url: window.location.href,
        });
        // Section scroll → voice announce: OFF for now (intrusive with TTS).
        // SPA page lands still announce via refreshLivingBar → announcePageForVoice.
        // Scroll continuity will return via text (sidebar) or a delayed voice mode later.
      },
    });
    // Initial sticky chrome for section 0 (placements stay classic 3 pills).
    // Double-rAF: ensure React useEffect has bound setSectionBarView first.
    pushSectionBarView(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => pushSectionBarView(lastSectionIndex < 0 ? 0 : lastSectionIndex));
    });
  }

  /**
   * Latest LLM (or lexical) prompt-bar lead. Null until first apply.
   * Grounded mini hop — never freestyle product claims.
   */
  let lastTextContinuityLead: string | null = null;
  /** Intent-ranked page pills (LLM) for engaged conversion stacks. */
  let lastRankedPagePills: string[] | null = null;
  let textContinuitySeq = 0;

  /**
   * Text continuity presentation for hero + side rail (cold / engaged).
   * Lexical baseline first; optional mini LLM may override the lead line.
   */
  function resolveTextPresentation() {
    const view = currentSectionView();
    const focus = readConversationFocus();
    const pool = unaskedPageQuestions(12);
    const base = resolveTextLandPresentation({
      focus,
      path: typeof window !== "undefined" ? window.location.pathname : "",
      pageTopic: pageTopic(),
      monologue: lastHeroMonologue,
      inviteLines: lastInviteLines,
      sectionHeadings: lastPageSections.map((s) => s.heading).filter(Boolean),
      currentHeading: view.active ? view.currentHeading : null,
      nextHeading: view.active ? view.nextHeading : null,
      pagePool: pool,
      currentQuestions: view.active ? view.currentQuestions : [],
      nextQuestions: view.active ? view.nextQuestions : [],
    });
    // Apply last LLM lead when present (still inventory pills only).
    if (lastTextContinuityLead) {
      return {
        ...base,
        heroLines: [lastTextContinuityLead],
        railLead:
          base.mode === "cold"
            ? base.railLead
            : lastTextContinuityLead.length <= 72
              ? lastTextContinuityLead
              : base.railLead,
        barLead: lastTextContinuityLead,
      };
    }
    return base;
  }

  function placementRenderOpts() {
    const text = resolveTextPresentation();
    // Lexical focus rank first; engaged LLM rank overrides when available.
    const lexical = text.rankedPagePills
      .map((q) => q.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    const llmRanked = (lastRankedPagePills ?? [])
      .map((q) => q.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    const ranked = (
      text.mode !== "cold" && llmRanked.length > 0 ? llmRanked : lexical
    ).slice(0, 3);
    const fallback = unaskedPageQuestions(3);
    const heroSummaryLines =
      text.heroLines.length > 0 ? text.heroLines : undefined;
    // Mid-page conversion lines: section pools (cold) + page pool (engaged).
    const sections = lastPageSections.map((s) => ({
      heading: s.heading,
      questions: filterUnaskedPageQuestions(s.questions ?? [], 6),
    }));
    return {
      heroMonologue: lastHeroMonologue,
      heroSummaryLines,
      pageQuestions: ranked.length > 0 ? ranked : fallback,
      sections,
      continuityMode:
        text.mode === "cold" ? ("cold" as const) : ("engaged" as const),
      // Scope-gated via pageVoiceMode(), exactly like the bubble's own voice
      // (see applyScope). The host-page placements render their OWN mic —
      // `showVoice` in placements.ts reads this flag — so passing the raw
      // tenant setting here left a live mic on a page whose rules denied
      // `voice`, while the in-panel voice was correctly silent. pageVoiceMode
      // reads the same cached verdict scopedDesign() masks placements with,
      // so the placement and the mic inside it can never disagree.
      voiceAssist: pageVoiceMode() !== "off",
      classicVoice: pageVoiceMode() === "classic",
      onHeroMonologueShown: () =>
        postToIframe({
          type: "event",
          event: "hero_monologue_shown",
          url: window.location.href,
        } as EventMessage),
      onHeroMonologueEngaged: () =>
        postToIframe({
          type: "event",
          event: "hero_monologue_engaged",
          url: window.location.href,
        } as EventMessage),
    };
  }

  /**
   * Push continuity lead to rail + bottom dock + hero/in-content presentation.
   * Bottom dock previously ignored LLM lead (template only) — that caused
   * "You asked about yes" while hero showed real summary.
   */
  function applyTextContinuity(h: BubbleHandle | null = handle) {
    try {
      const text = resolveTextPresentation();
      // Prefer LLM line for dock; fall back to resolver railLead.
      const dockLead =
        (lastTextContinuityLead && lastTextContinuityLead.trim()) ||
        text.barLead ||
        text.railLead;
      h?.setRailContinuityLead?.(dockLead);
      h?.setDockContinuityLead?.(dockLead);
    } catch {
      /* ignore */
    }
  }

  /** Same page? WS bar pushes fan out to every open tab; each applies only its
   *  own page's bar (the pills/lead are page-specific). */
  function samePagePath(a: string): boolean {
    try {
      return new URL(a).pathname.replace(/\/+$/, "") ===
        window.location.pathname.replace(/\/+$/, "");
    } catch {
      return false;
    }
  }

  /** Fingerprint of the last-applied server bar — dedup so the same content
   *  arriving on BOTH the events response and the WS push renders only once. */
  // NOTE (nudge rewrite, 2026-08-09): the server no longer composes/pushes
  // `bar` (bar-compose deleted), so applyServerBar/engagedRanked below are
  // permanently dormant — kept only until the later widget cleanup pass.
  let lastServerBarKey = "";
  let nudgeScroll: NudgeScrollHandle | null = null;

  /**
   * Apply the SERVER-composed living bar (ranked pills + continuity lead). It
   * arrives on the pageview events response AND over the WS (cross-tab sync);
   * this dedups so identical content re-renders only once. Replaces the old
   * client rank/text-continuity POSTs — lexical first, then swap to this.
   */
  function applyServerBar(pills: string[], lead: string, url: string, h: BubbleHandle | null = handle) {
    const clean = (lead ?? "").replace(/\s+/g, " ").trim();
    const key = `${url} ${clean} ${pills.join("")}`;
    if (key === lastServerBarKey) return; // identical to what's shown — leave it
    lastServerBarKey = key;
    if (pills.length > 0) {
      lastRankedPagePills = pills;
      // Shared next-ask list for panel Suggested / voice (always up to 3).
      // Server already metered this compose — no client re-rank, no free TTS.
      h?.setRankedNextAsks?.(pills);
      // Rail: only switch to engaged "Next for you" when chat focus exists;
      // cold compose keeps full section inventory (fixes 3→2 shrink).
      pushSectionBarView(lastSectionIndex < 0 ? 0 : lastSectionIndex);
    }
    if (clean) lastTextContinuityLead = clean;
    applyTextContinuity(h);
    if (lastDesign && customerId && h) {
      placementObserver?.disconnect();
      placementTwBoundKey = "";
      renderPlacements(customerId, scopedDesign()!, placementActivate(), placementRenderOpts());
      applyPlacementTypewriters(lastPlacementTwTexts, true);
      placementObserver?.observe(document.body, { childList: true, subtree: true });
    }
  }

  /**
   * Mini LLM grounded lead (same family as voice pill rank / voice-summary).
   * Fleet-wide — materials from page-voice + conversation focus only.
   * On miss/timeout keeps lexical presentation.
   */
  // Server-authoritative: the ranked pills + continuity lead are now composed
  // server-side on the pageview event and pushed over the WS (applyServerBar).
  // The client no longer POSTs rank-page-questions / text-continuity; the
  // lexical baseline is applied by applyTextContinuity. Kept as a no-op so its
  // several callers (page load / cache reapply) need no change; deleted in P5.
  function refreshTextContinuityLlm(h: BubbleHandle | null = handle) {
    void h;
  }

  /** Rebuild bar lines + placement chips from cached page-voice (no network). */
  function reapplyLivingBarFromCache(h: BubbleHandle | null = handle) {
    // Full pool → bubble refills panel/suggested to 3 unasked after each ask.
    h?.setPageQuestionPool?.(lastPageQuestions);
    // Re-seed shared next-asks AFTER pool update. applyServerBar may have already
    // set these; reapply (focus update / page-voice) must not leave Suggested on
    // the old diversify path while voice still has the server/voice list.
    if (lastRankedPagePills && lastRankedPagePills.length > 0) {
      h?.setRankedNextAsks?.(lastRankedPagePills);
    }
    // Cold Click-to-talk summary material (scenario 1).
    h?.setPageVoiceMeta?.({
      heroMonologue: lastHeroMonologue ?? [],
      inviteLines: lastInviteLines ?? [],
      sectionHeadings: lastPageSections.map((s) => s.heading).filter(Boolean),
    });
    // Sticky living bar: section dual-chrome (scroll-aware). Hero stays classic.
    pushSectionBarView(lastSectionIndex < 0 ? 0 : lastSectionIndex);
    // Text continuity: lexical first, then mini LLM lead (async, grounded).
    applyTextContinuity(h);
    refreshTextContinuityLlm(h);
    // Bar typewriter: summary voice only — questions live on cards/pills.
    const lines = buildBarQueue({
      inviteLines: lastInviteLines,
    });
    h?.setBarLines?.(lines);
    lastPlacementTwTexts = lines.map((l) => l.text);
    if (lastDesign && customerId) {
      // Pause observer so our replaceChildren don't schedule a second render.
      placementObserver?.disconnect();
      placementTwBoundKey = "";
      renderPlacements(customerId, scopedDesign()!, placementActivate(), placementRenderOpts());
      applyPlacementTypewriters(lastPlacementTwTexts, true);
      placementObserver?.observe(document.body, { childList: true, subtree: true });
    } else {
      applyPlacementTypewriters(lastPlacementTwTexts, true);
    }
  }

  /** Apply a page-voice payload (either transport) to the living bar. */
  function applyPageVoicePayload(
    h: BubbleHandle | null,
    v: { pageQuestions?: string[]; sections?: PageSection[]; inviteLines?: string[]; heroMonologue?: string[] },
  ) {
    // Store FIRST — a pre-mount arrival keeps its data; wireLivingBar
    // replays it into the DOM once the bubble exists.
    lastHeroMonologue = v.heroMonologue;
    lastPageQuestions = v.pageQuestions ?? [];
    lastPageSections = v.sections ?? [];
    lastInviteLines = v.inviteLines;
    // Content-completeness signal: the payload LANDED (even empty — a page
    // with no sections keeps the sparse row as its final look and paints
    // now). Latched so a post-payload mount replays it.
    pageVoiceLanded = true;
    if (!h) return;
    h.setSectionDataLanded?.();
    // Preload section data only — visibility is owned by scroll-dock
    // (hidden until first scroll when an in-page hero/line is present).
    restartSectionScroll();
    // CRITICAL ORDER: rebuild placement DOM first, THEN attach typewriters.
    // apply-before-render wiped .bhai-tw-target and left hero stuck on
    // static askAboutCopy + design chips (bottom bar was fine — React path).
    reapplyLivingBarFromCache(h);
    // Re-evaluate dock now that placements/sections may exist.
    scrollDock?.refresh();
    maybeAnnounceAfterPageVoice(h);
    debugLog(
      "[BlueHat] living bar: pageQuestions =",
      lastPageQuestions.length,
      "sections =",
      lastPageSections.length,
      "(sticky when scrolled past hero)",
    );
  }

  // #594: page-voice rides the pageview EVENT response (pagevoice:update)
  // EXCLUSIVELY — the host-page fetch and its endpoint are deleted. An old
  // cached bh.js still fetching the dead endpoint degrades to empty lines
  // (its own catch) until its cache refreshes.
  // Route-change announce: fires when the pageview response lands; a short
  // announce-only timer covers a lost/failed pageview POST (the old fetch
  // path announced even on failure — behavior preserved, no fetch).
  let announceAfterPageVoicePending = false;
  let announceFallbackTimer: number | null = null;
  function maybeAnnounceAfterPageVoice(h: BubbleHandle | null) {
    if (!announceAfterPageVoicePending || widgetDisposed || !h) return;
    announceAfterPageVoicePending = false;
    const view =
      lastPageSections.length > 0
        ? buildSectionBarView(lastPageSections, lastSectionIndex < 0 ? 0 : lastSectionIndex)
        : null;
    h.announcePageForVoice?.(view && view.active ? view : null);
  }
  function armAnnounceFallback(h: BubbleHandle | null) {
    if (announceFallbackTimer != null) window.clearTimeout(announceFallbackTimer);
    announceFallbackTimer = window.setTimeout(() => {
      announceFallbackTimer = null;
      maybeAnnounceAfterPageVoice(h);
    }, 5000);
  }

  /** Re-apply whatever page-voice state is stored — covers a pagevoice:update
   *  that raced ahead of the mount (the payload lands in module vars either
   *  way; the DOM work replays here). */
  function replayPageVoice(h: BubbleHandle | null) {
    if (!h) return;
    if (pageVoiceLanded) h.setSectionDataLanded?.();
    restartSectionScroll();
    reapplyLivingBarFromCache(h);
    scrollDock?.refresh();
    maybeAnnounceAfterPageVoice(h);
  }

  /** Typewriter for hero_bar / in_content_line / end_of_page_banner collapsed copy. */
  const placementTwCleanups: Array<() => void> = [];
  let placementTwBoundKey = "";
  function applyPlacementTypewriters(texts: string[], force = false) {
    if (!customerId || texts.length === 0) return;
    const id = CSS.escape(customerId);
    const nodes = document.querySelectorAll(
      `div.bhai-${id}-hero .bhai-tw-target, div.bhai-${id}-line .bhai-tw-target, div.bhai-${id}-cta .bhai-tw-target`,
    );
    // Skip restart when the same queue is already driving the same targets
    // (avoids flash loops if something re-invokes apply without a real change).
    const key = `${texts.join("\0")}|${nodes.length}`;
    if (!force && key === placementTwBoundKey && placementTwCleanups.length === nodes.length) {
      return;
    }
    for (const c of placementTwCleanups) c();
    placementTwCleanups.length = 0;
    placementTwBoundKey = key;
    for (const node of nodes) {
      const el = node as HTMLElement & {
        __bhaiTw?: ReturnType<typeof createTypewriter>;
      };
      const tw = createTypewriter(() => el);
      tw.setLines(texts.map((t) => ({ text: t, kind: "page_question" as const })));
      // Stash handle so placement click can submit the FULL line (not partial text).
      el.__bhaiTw = tw;
      placementTwCleanups.push(() => {
        tw.stop();
        if (el.__bhaiTw === tw) delete el.__bhaiTw;
      });
    }
  }

  /** Shared open / open+submit for every renderPlacements call site. */
  function placementActivate() {
    return {
      open: () => handle?.open(),
      submitLine: (text: string) => {
        if (handle?.openWithText) handle.openWithText(text);
        else {
          handle?.open();
          // Fallback if openWithText not wired yet
          queueMicrotask(() => callbacks.onSend(text));
        }
      },
      /** "Get the answers now" — open panel and show those section Qs as pills. */
      openWithQuestions: (questions: string[]) => {
        if (handle?.openWithSectionQuestions) {
          handle.openWithSectionQuestions(questions);
          return;
        }
        handle?.open();
        // Soft fallback: open only (panel will show page pills).
        void questions;
      },
      /** Hero / placement AI Voice → same session toggle as bar + FAB. */
      toggleVoice: () => handle?.toggleAiVoice?.(),
    };
  }

  // --- Mount state machine ---
  let handle: BubbleHandle | null = null;
  let targetObserver: MutationObserver | null = null;
  let stickyObserver: IntersectionObserver | null = null;
  // #592 inline placement: tenant mode latch (null until config — the
  // fail-closed gap) and the currently-mounted blessed div.
  let lastInlineMode: InlineMode | null = null;
  /** Tenant's inline close-button setting; false until config says otherwise. */
  let lastInlineClose = false;
  /** When the LOWER BAR may appear next to inline containers. Until config
   *  lands we assume the shipped behaviour, so nothing flickers off. */
  let lastBarMode: BarMode = DEFAULT_BAR_MODE;
  /** What the lower bar shows. Until config lands, the full bar — the bar is
   *  held back until then anyway (#589), so nothing paints the wrong form. */
  let lastBarContent: BarContent = DEFAULT_BAR_CONTENT;
  let lastInlineTarget: HTMLElement | null = null;
  /** This visitor's variants in the running A/B tests (ab-tests.ts). Resolved
   *  on every config; the stored draw keeps it the same across pages. */
  let abState: AbState = NO_AB;
  const reportExposure = createExposureReporter((pick, url) =>
    postToIframe({
      type: "event",
      event: EXPERIMENT_EXPOSURE_EVENT,
      experiment_id: pick.experimentId,
      variant: pick.variant,
      surface: pick.surface,
      url,
    }),
  );
  let bannerObserver: MutationObserver | null = null;
  // Last chatEnabled from the SW `config` message, remembered so it survives the
  // race where config arrives BEFORE the bubble mounts. The bubble mounts on
  // DOMContentLoaded, but the SW handshake can finish first — and the old
  // `handle?.setVisible(...)` SILENTLY dropped config when handle was null, so
  // the widget never revealed ("loads sometimes"). Re-applied on every mount.
  let lastChatEnabled: boolean | null = null;
  // Generated design + display mode (from the config message). Default-safe:
  // bubble + no design = the classic widget, unchanged.
  let lastDisplay: "bubble" | "side_panel" = "bubble";
  let lastDesign: WidgetRuntimeDesign | null = null;
  /** null until preload/config — never paint default sticky_notes before the real skin. */
  let lastLivingBarSkin: LivingBarSkin | null = null;
  // #589: config-landed + tenant reveal option, seeded into the scroll-dock
  // (which FAILS CLOSED until the config lands — the pre-config frames used
  // to paint the unskinned, un-scoped default bar).
  let lastConfigLanded = false;
  let lastRevealOnScroll = false;
  let placementObserver: MutationObserver | null = null;
  // Set once the widget is torn down (domain rejected) so the SPA route-change
  // hook stops emitting pageviews / re-arming triggers after disposal.
  let widgetDisposed = false;

  /** After a Sales Brain turn writes focus, refresh hero + rail text continuity. */
  function onConversationFocusUpdated() {
    if (widgetDisposed) return;
    reapplyLivingBarFromCache(handle);
  }
  window.addEventListener("bhai-conversation-focus", onConversationFocusUpdated);

  function applyChatEnabled() {
    if (lastChatEnabled !== null) handle?.setVisible(lastChatEnabled);
  }

  // Read the per-page banner div's data-* attributes, or null when no div is
  // present on this page (→ no banner, floating circle as usual). This is the
  // ONLY source of banner content — styling never comes from a server callback;
  // the widget fills any omitted field from a single canned default.
  function readBannerDiv(): BannerConfig | null {
    if (!bannerSelector) return null;
    const el = document.querySelector<HTMLElement>(bannerSelector);
    if (!el) return null;
    const d = el.dataset;
    // CONTENT only — colors/theme are set via --bhjs-banner-* CSS variables,
    // not data-* (consistent with the chat's CSS theming).
    const o: BannerConfig = {};
    if (d.headline) o.headline = d.headline;
    if (d.subtext) o.subtext = d.subtext;
    if (d.button) o.buttonLabel = d.button;
    return o;
  }

  function refreshBanner() {
    handle?.setBanner(readBannerDiv());
  }

  function setupBannerObserver() {
    if (!bannerSelector || bannerObserver) return;
    // Re-evaluate on DOM changes so a late-rendered (or SPA-removed) banner
    // div toggles the banner on/off, like the inline-target watcher.
    bannerObserver = new MutationObserver(() => refreshBanner());
    bannerObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Re-render placement launchers when their anchor divs appear/change
  // (SPA-rendered targets land after the config message). Two safeguards keep
  // this SPA-safe:
  //  1) Pause the observer while we write to the anchors. renderPlacements'
  //     own replaceChildren/appendChild are childList mutations INSIDE the
  //     observed subtree, so otherwise every render re-triggers the observer
  //     in an endless loop — which froze the page on client-side navigation.
  //  2) Coalesce bursts to one render per animation frame (a route change
  //     churns the DOM heavily; we don't need to re-render on every mutation).
  /**
   * `lastDesign` with every scope-denied placement forced to enabled=false.
   *
   * This is the whole placement gate, and it needs no new render path:
   * `renderPlacements` calls `anchor.replaceChildren()` BEFORE it checks
   * `placement.enabled` (placements.ts:145-146), so feeding it a masked design
   * CLEARS the anchors rather than leaving stale nodes. Re-enabling is equally
   * free — the next render sees the unmasked design and refills them.
   *
   * Returns `lastDesign` unchanged when nothing is denied, so the common path
   * allocates nothing.
   */
  function scopedDesign(): WidgetRuntimeDesign | null {
    if (!lastDesign) return null;
    const gates = currentGates();
    // The in-page bar's three surface names are aliases of one bar, so a rule
    // hiding any one of them hides all three.
    const inPageAllowed = IN_PAGE_BAR_KEYS.every((k) => gates[k]);
    if (gates.nav_launcher && inPageAllowed) return lastDesign;
    const placements = { ...lastDesign.placements };
    if (!gates.nav_launcher) placements.nav_launcher = { ...placements.nav_launcher, enabled: false };
    if (!inPageAllowed) {
      for (const k of IN_PAGE_BAR_KEYS) placements[k] = { ...placements[k], enabled: false };
    }
    return { ...lastDesign, placements };
  }

  /**
   * Re-resolve the per-URL scope and push it to every surface. THE single
   * entry point — config arrival, mount, and SPA navigation all come through
   * here, so there is exactly one place that decides what shows.
   *
   * Every surface rides an EXISTING setter. Nothing new was added to
   * bubble.tsx for v1, which is why this is cheap and why re-enabling works
   * for free: the same setters carry the un-denied values back.
   *
   * Note what is deliberately NOT gated: pageview and trigger events. Denying
   * a URL means "show nothing here", not "stop counting the visit" — a tenant
   * hiding the widget on /checkout still wants the traffic. (If the URL itself
   * is the sensitive thing, that is a different feature.)
   */
  function applyScope(reason: "config" | "mount" | "route" | "terminate" | "viewport"): void {
    const gates = evaluateScopeForCurrentUrl();

    // HOST-LEVEL hide for the two ALL-OFF verdicts (phones-off, master kill).
    // The per-surface gates below cannot reach a mode-"open" container: its
    // chat renders regardless of barAllowed (deliberate for per-URL scope
    // rules — pinned "open mode is the chat, always"). Hiding the HOST is the
    // one seam that covers every surface, and it is reversible: the React
    // tree, chat state and WS stay alive underneath, and display returns on
    // the next evaluation (un-kill config, or crossing back over the
    // breakpoint). This also closes the pre-existing kill-switch gap where an
    // always-open container's chat survived terminate.
    if (handle) handle.host.style.display = isPhoneHidden() || isMasterOff() ? "none" : "";

    // ONE voice push: the page-gated MODE. The bubble derives everything else
    // (voiceAssist, classicVoice) from it, so a denied page — mode "off" —
    // closes every voice surface and every voice behavior at once. There is
    // deliberately no separate setVoiceAssist: two writable copies of one
    // fact, only one wired to this gate, is how the classic-UI mic leak
    // happened.
    handle?.setVoiceMode?.(pageVoiceMode());

    // Bar: its OWN gate, not the design's `enabled` flag and not
    // setStickyBarVisible.
    //
    // Not the design, because `setBottomBar(null)` means "no generated design,
    // use defaults" and the render gate reads it as `?? true` — so on a tenant
    // that has never run Widget Design (design === null, the DEFAULT state) a
    // deny rule could not express "hidden" at all and silently did nothing.
    // Caught on vacuums.test: the tester said Hide, the site showed the bar.
    //
    // Not setStickyBarVisible either, because that flag belongs to scroll-dock,
    // which calls refresh() from renderPlacementsGuarded below and would win
    // the next scroll event.
    // The bar's own gate, now carrying BOTH verdicts: the per-URL scope rule
    // and the tenant's lower-bar mode (always / unless_inline / inline_only).
    // One seam, so a deny from either side reads the same downstream.
    // TWO verdicts, deliberately NOT merged (bench 2026-08-24): scope deny
    // means "no chrome on this page" and applies to every surface; the
    // lower-bar mode is a question about the FLOATING bar only. Folding them
    // into one flag made `unless_inline` suppress the inline container's own
    // collapsed bar — the surface that mode exists to defer TO — so an
    // expand-mode container painted nothing at all.
    handle?.setBarSuppressed?.(!gates.bar);
    handle?.setLowerBarModeAllows?.(lowerBarAllowed(lastBarMode, findInlineTarget() != null));
    handle?.setBarNudgesOnly?.(lastBarContent === "nudges");

    reportAbExposures(gates.bar);

    // Banner is per-page already (it needs its anchor div); this scopes it by
    // URL as well.
    handle?.setBanner(gates.banner ? readBannerDiv() : null);

    // Placements clear or refill through the masked design — see scopedDesign.
    renderPlacementsGuarded();

    // masterOff included ON PURPOSE: the kill verdict is reconciled inside the
    // iframe, whose console and localStorage are a different origin. Reporting
    // it here means the page you are debugging on shows whether the widget
    // thinks it is killed, without cross-frame archaeology.
    debugLog(
      "[BlueHat] scope:",
      reason,
      window.location.pathname,
      isMasterOff() ? "MASTER OFF" : "master on",
      gates,
    );
  }

  let placementRenderQueued = false;
  function renderPlacementsGuarded() {
    placementRenderQueued = false;
    if (!lastDesign || !customerId) return;
    placementObserver?.disconnect();
    // Re-assert the theme too: applyTheme is idempotent and re-adopts its sheet
    // if an SPA route change dropped it from document.adoptedStyleSheets.
    applyTheme(lastDesign.theme);
    // Force typewriter re-bind: nodes are brand-new after replaceChildren.
    placementTwBoundKey = "";
    renderPlacements(customerId, scopedDesign()!, placementActivate(), placementRenderOpts());
    // DOM just rebuilt — re-bind typewriters to the new .bhai-tw-target nodes.
    if (lastPlacementTwTexts.length > 0) applyPlacementTypewriters(lastPlacementTwTexts, true);
    placementObserver?.observe(document.body, { childList: true, subtree: true });
    // Anchors may have just gained/lost children (SPA nav) — re-anchor the dock.
    scrollDock?.refresh();
  }
  function setupPlacementObserver() {
    if (placementObserver || !customerId) return;
    placementObserver = new MutationObserver((records) => {
      // Typewriter paints via textContent on .bhai-tw-target, which replaces text
      // nodes (childList). If we re-render on those, we restart the typewriter
      // every frame → super-fast flashing. Only care about anchors appearing or
      // host DOM outside our launchers (SPA nav, late React mounts).
      const relevant = records.some((rec) => {
        const t = rec.target;
        if (!(t instanceof Element)) {
          // Text node under a launcher — ignore.
          const p = t.parentElement;
          return !p || !p.closest(".bhai-launcher, .bhai-tw-target");
        }
        if (t.closest(".bhai-launcher, .bhai-tw-target")) return false;
        for (const n of rec.addedNodes) {
          if (n instanceof Element && n.closest?.(".bhai-launcher")) continue;
          // Our own widget element (class bhai-widget) is not a page anchor.
          if (n instanceof Element && n.classList?.contains("bhai-widget")) continue;
          if (n instanceof Element && n.className && String(n.className).includes("bhai-")) return true;
          if (n instanceof Element && n.querySelector?.("[class*='bhai-']")) return true;
        }
        for (const n of rec.removedNodes) {
          if (n instanceof Element && n.closest?.(".bhai-launcher")) continue;
          if (n instanceof Element && n.classList?.contains("bhai-widget")) continue;
          if (n instanceof Element && n.className && String(n.className).includes("bhai-")) return true;
        }
        // Empty bhai-* anchor gained/lost children from outside our launcher write?
        if (t.classList && [...t.classList].some((c) => c.startsWith("bhai-") && !c.includes("launcher"))) {
          // Only if the mutation is NOT just our launcher (already filtered above
          // when target is inside launcher). Anchor itself getting children from
          // React wipe (empty again) is relevant.
          return true;
        }
        return false;
      });
      if (!relevant) return;
      if (placementRenderQueued) return;
      placementRenderQueued = true;
      requestAnimationFrame(renderPlacementsGuarded);
    });
    placementObserver.observe(document.body, { childList: true, subtree: true });
  }

  function stopStickyObserver() {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
  }

  // Toggle data-stuck inside a View Transition when supported, so the
  // position:static → position:fixed swap morphs smoothly instead of
  // snapping. The browser snapshots the host's old + new geometry and
  // cross-fades/animates between them automatically.
  function applyStuck(host: HTMLElement, stuck: boolean) {
    const apply = () => {
      if (stuck) host.setAttribute("data-stuck", "");
      else host.removeAttribute("data-stuck");
    };
    const start = (document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    }).startViewTransition;
    if (typeof start === "function") {
      start.call(document, apply);
    } else {
      apply();
    }
  }

  // Inline mode: once the customer's container scrolls above the viewport,
  // pop the host out to a fixed corner window. Observing the customer's div
  // (not the host) avoids feedback loops — a position:fixed host would
  // appear "always visible" and toggle endlessly.
  function setupStickyObserver(host: HTMLElement, target: HTMLElement) {
    stopStickyObserver();
    let current = false;
    stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (stuck === current) return;
        current = stuck;
        applyStuck(host, stuck);
      },
      { threshold: 0 },
    );
    stickyObserver.observe(target);
  }

  /**
   * A/B exposure: report a test only where its surface is actually in play on
   * this page — the floating lower bar allowed here, or an inline container
   * that follows the site's default style (one that names its own style with
   * data-bhai-inline is not in the test). Forced views (?bh_ab=) never report.
   */
  function reportAbExposures(barGate: boolean): void {
    if (!lastConfigLanded || isMasterOff() || isPhoneHidden()) return;
    const url = window.location.href.split("#")[0];
    const inlineTarget = lastInlineTarget;
    if (
      !inlineTarget &&
      barGate &&
      lowerBarAllowed(lastBarMode, findInlineTarget() != null) &&
      (lastDesign?.placements.bottom_bar?.enabled ?? true)
    ) {
      reportExposure(abState.picks.lower_bar, url);
    }
    if (inlineTarget && !isInlineMode(inlineTarget.getAttribute("data-bhai-inline"))) {
      reportExposure(abState.picks.inline, url);
    }
  }

  function findInlineTarget(): HTMLElement | null {
    return inlineSelector ? document.querySelector<HTMLElement>(inlineSelector) : null;
  }

  function stopTargetObserver() {
    if (targetObserver) {
      targetObserver.disconnect();
      targetObserver = null;
    }
  }

  /** Effective inline mode for one blessed div: a valid data-bhai-inline
   *  attribute is known at mount (no fail-closed gap); otherwise the tenant
   *  mode from config, null until it lands. */
  function resolveInlineMode(target: HTMLElement): InlineMode | null {
    const attr = target.getAttribute("data-bhai-inline");
    if (isInlineMode(attr)) return attr;
    return lastInlineMode == null ? null : inlineModeFor(lastInlineMode, attr);
  }

  /** Stuck-mode (scroll-away → fixed corner) is an always-open-chat
   *  affordance — a collapsed bar must scroll naturally, so only mode
   *  "open" arms the observer (#592). */
  function applyInlineMode() {
    if (!handle || !lastInlineTarget) return;
    const mode = resolveInlineMode(lastInlineTarget);
    if (mode != null) handle.setInlineMode?.(mode);
    if (mode === "open") {
      setupStickyObserver(handle.host, lastInlineTarget);
    } else {
      stopStickyObserver();
      handle.host.removeAttribute("data-stuck");
    }
  }

  function mountInline(target: HTMLElement) {
    stopTargetObserver();
    if (handle) {
      handle.unmount();
      handle = null;
    }
    lastInlineTarget = target;
    handle = createBubble(callbacks, {
      mode: "inline",
      target,
      inlineMode: resolveInlineMode(target),
      chipsAllowed: inlineChipsAllowed(target.getAttribute("data-bhai-chips")),
      // Seed the skin so a post-config mount paints styled on frame 1
      // (the setter-only carry restyled a frame late — bench 2026-08-17).
      initialLivingBarSkin: lastLivingBarSkin,
      // Paint permission: pre-config the LOOK is unknown even when the div
      // attribute reveals the mode — the surface stays blank until config.
      initialConfigLanded: lastConfigLanded,
      initialInlineClose: lastInlineClose,
    });
    applyInlineMode();
  }

  function mountFloatingWithWatcher() {
    // Seed banner + visibility synchronously so the bubble paints correctly on
    // first render: initialVisible carries any chatEnabled config that arrived
    // before this mount (the race), so we don't depend on a post-mount
    // setVisible() that fires before React wires the _setVisible ref.
    handle = createBubble(callbacks, { mode: "floating" }, readBannerDiv(), lastChatEnabled === true);
    if (!inlineSelector) return;
    targetObserver = new MutationObserver(() => {
      const t = findInlineTarget();
      if (t) mountInline(t);
    });
    targetObserver.observe(document.body, { childList: true, subtree: true });
  }

  function wireLivingBar(h: BubbleHandle | null) {
    if (!h || !customerId) return;
    // Prefetch page-voice while bar may be hidden under the hero.
    h.setStickyBarVisible?.(false);
    scrollDock?.disconnect();
    scrollDock = setupScrollDock({
      customerId,
      configReady: lastConfigLanded,
      revealOnScroll: lastRevealOnScroll,
      onStickyChange: (vis) => {
        h.setStickyBarVisible?.(vis);
        // When sticky docks (scrolled past hero), ensure section chrome is current.
        if (vis && lastPageSections.length > 0) {
          pushSectionBarView(lastSectionIndex < 0 ? 0 : lastSectionIndex);
        } else {
          clearRetiredSectionStage();
        }
      },
    });
    replayPageVoice(h);
  }

  function initialMount() {
    const target = findInlineTarget();
    if (target) {
      lastInlineTarget = target;
      handle = createBubble(callbacks, {
      mode: "inline",
      target,
      inlineMode: resolveInlineMode(target),
      chipsAllowed: inlineChipsAllowed(target.getAttribute("data-bhai-chips")),
      // Seed the skin so a post-config mount paints styled on frame 1
      // (the setter-only carry restyled a frame late — bench 2026-08-17).
      initialLivingBarSkin: lastLivingBarSkin,
      // Paint permission: pre-config the LOOK is unknown even when the div
      // attribute reveals the mode — the surface stays blank until config.
      initialConfigLanded: lastConfigLanded,
      initialInlineClose: lastInlineClose,
    });
      applyInlineMode();
    } else {
      lastInlineTarget = null;
      mountFloatingWithWatcher();
    }
    // Carry any display/design that arrived before this mount (config race).
    handle?.setDisplay(lastDisplay);
    handle?.setBottomBar(lastDesign?.placements.bottom_bar ?? null);
    // Only apply when config already landed — applying DEFAULT sticky_notes here
    // caused a one-frame wrong-skin flash before the real dashboard skin arrived.
    if (lastLivingBarSkin != null) handle?.setLivingBarSkin?.(lastLivingBarSkin);
    wireLivingBar(handle);
    if (lastDesign && customerId) {
      setupPlacementObserver();
      // Scope owns theme + placements + the bar/voice/banner re-push. When
      // config has not landed yet this is skipped and the `config` case
      // applies it on arrival — either order ends in one applyScope.
      applyScope("mount");
    }
    // Banner is orthogonal to inline/floating — watch for its div on every
    // page and reflect add/remove (the bubble only renders it in floating
    // mode; initial state is seeded at createBubble above).
    setupBannerObserver();
    // (Config that arrived before this mount is carried into the bubble via
    // createBubble's initialVisible above — NOT a post-mount setVisible(), which
    // would race the React effect that wires _setVisible.)
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialMount, { once: true });
  } else {
    initialMount();
  }

  // First-party host copy of conversation id — survives third-party iframe
  // storage gaps on full navigations (pricing empty-thread miss).
  // HOST_SESSION_KEY defined at top of IIFE (shared with new-session clear).
  /** Host localStorage, or null where touching it throws (sandboxed frames,
   *  some privacy modes). */
  function hostLocalStorage(): Storage | null {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }

  function readHostSession(): string | null {
    try {
      return localStorage.getItem(HOST_SESSION_KEY);
    } catch {
      return null;
    }
  }
  function writeHostSession(sid: string) {
    if (!sid) return;
    // Never re-store the sid we just wiped (in-flight preload race).
    if (wipedHostSid && sid === wipedHostSid) {
      console.warn(
        "[BlueHat] host refuse wiped sid",
        sid.slice(0, 8),
        "— waiting for mint",
      );
      return;
    }
    // After ?bh_new_session=1 accept the first *new* server mint only.
    if (forceNewSession) {
      try {
        localStorage.setItem(HOST_SESSION_KEY, sid);
      } catch {
        /* private mode */
      }
      forceNewSession = false;
      wipedHostSid = null;
      debugLog("[BlueHat] host adopted fresh sid", sid.slice(0, 8));
      return;
    }
    try {
      const existing = localStorage.getItem(HOST_SESSION_KEY);
      // Never clobber a known host sid with a remint from a racey preload.
      if (existing && existing !== sid) {
        console.warn("[BlueHat] host session keep (ignore remint)", {
          keep: existing.slice(0, 8),
          dropped: sid.slice(0, 8),
        });
        return;
      }
      localStorage.setItem(HOST_SESSION_KEY, sid);
    } catch {
      /* private mode */
    }
  }

  // Send init + pageview once iframe is ready
  iframe.addEventListener("load", () => {
    iframeReady = true;
    const hostSid = forceNewSession ? null : readHostSession();
    postToIframe({
      type: "init",
      hostDomain: window.location.hostname,
      ...(hostSid ? { sessionId: hostSid } : {}),
      ...(forceNewSession ? { newSession: true } : {}),
    });
    postToIframe({
      type: "event",
      event: "pageview",
      url: window.location.href,
      referrer: document.referrer,
    });

    // If admin mode, tell iframe to use cache-busting
    if (admin) {
      postToIframe({ type: "admin:enable" });
    }

    // Flush anything queued before iframe was ready (e.g. inline mode's
    // immediate chat:open fired during DOMContentLoaded).
    while (pendingToIframe.length > 0) {
      const msg = pendingToIframe.shift();
      if (msg) iframe.contentWindow?.postMessage(msg, origin);
    }
  });

  function onTriggerEvent(msg: EventMessage) {
    postToIframe(msg);
    // In admin mode, visually mark triggered elements
    if (admin && activeTriggers && "triggerId" in msg && msg.triggerId) {
      markTriggered(msg.triggerId as string, activeTriggers);
    }
  }

  function handleTriggers(triggers: Trigger[]) {
    // Clean up previous observers
    if (observerCleanup) observerCleanup();
    if (adminStyleCleanup) adminStyleCleanup();

    activeTriggers = triggers;

    if (triggers.length === 0) return;
    // Nothing to watch for if nothing may be reported. Skipping the install
    // also spares the page the MutationObserver the dwell/form watchers need.
    if (telemetryOff) return;

    if (admin) {
      adminStyleCleanup = injectAdminStyles(triggers);
    }

    observerCleanup = setupObservers(triggers, onTriggerEvent);
  }

  // Listen for messages from the iframe
  window.addEventListener("message", (event) => {
    if (event.origin !== origin) return;
    const data = event.data as IframeMessage;

    switch (data.type) {
      case "debug":
        // The iframe's own log line (debug mode only — it sends none otherwise).
        console.log("[BlueHat iframe]", data.line);
        break;
      case "session":
        // Mirror iframe conversation id into first-party host storage so the
        // next full navigation re-seeds the same sid on init.
        if (data.sessionId) writeHostSession(data.sessionId);
        break;
      case "chat:init": {
        // A WS reconnect delivers the authoritative stored history — but a turn
        // is persisted only when it COMPLETES (widget-api chat-persist.ts writes
        // the visitor + agent rows together at the end). So a chat:init that
        // lands mid-turn legitimately lacks the question the visitor is waiting
        // on, and a blind replace would erase it from the screen and leave the
        // transcript ending on the PREVIOUS turn's answer — which reads as
        // "answered", clears the caret, and un-gates the pills over an
        // unanswered question. That is the 2026-07-30 nemesis, and a harness
        // run reproduced it on every healthy turn (answers take ~9s; any
        // reconnect inside that window did it).
        //
        // Rule: stored history never erases a turn still in flight, and only
        // an answer to THIS visitor's pending question ends the wait.
        const merged = data.messages.slice();
        const pending = data.pendingQuestion ?? null;
        if (pending && !historyAnswersAsk(data.messages, pending)) {
          if (!merged.some((m) => m.sender === "client" && m.text.trim() === pending.trim())) {
            merged.push({
              // Deterministic id when the iframe knows the send's correlation
              // id: the server echo carries the same clientMsgId and adopts
              // this exact row. Date.now() only for an older iframe bundle
              // that predates pendingMsgId (text-match adoption fallback).
              id: data.pendingMsgId ? placeholderIdFor(data.pendingMsgId) : `${PENDING_ID_PREFIX}${Date.now()}`,
              sender: "client",
              text: pending,
              createdAt: Date.now(),
            });
          }
        }
        handle?.setMessages(merged);
        // Ask the predicate, not "what is last". A trailing text-less agent
        // row is an answerChips carrier, not a reply — see pending-answer.ts.
        if (historyAnswersAsk(merged, pending)) {
          handle?.setThinking(false);
        }
        break;
      }
      case "chat:new":
        handle?.addMessage(data.message);
        break;
      case "chat:update":
        handle?.updateMessage(data.message);
        break;
      case "chat:thinking":
        handle?.setThinking(data.thinking);
        break;
      case "voice:play":
        // Server-assembled voice mp3. A host-line intent carries a correlation
        // token → route to the requesting trigger's continuation; nav-sourced
        // voice (pageview monologue/reservation) has none → playNavVoice.
        if (data.intentId) {
          handle?.playHostVoice?.(data.intentId, data.mp3Url);
        } else {
          handle?.playNavVoice?.(data.mp3Url);
        }
        break;
      case "bar:update":
        // Server-composed living bar (ranked pills + continuity lead) for a page
        // land, pushed over the WS. Apply only if it's for the page THIS tab is
        // on (the push fans out to every open tab for cross-page sync).
        if (samePagePath(data.url)) {
          applyServerBar(data.pills, data.lead, data.url);
        }
        break;
      case "pagevoice:update":
        // Page-voice text from the pageview event response (#594) — the
        // same data the legacy host-page fetch returned, minus the fetch.
        if (samePagePath(data.url)) {
          if (announceFallbackTimer != null) {
            window.clearTimeout(announceFallbackTimer);
            announceFallbackTimer = null;
          }
          applyPageVoicePayload(handle, {
            inviteLines: data.inviteLines,
            heroMonologue: data.heroMonologue,
          });
        }
        break;
      case "nudges:update":
        // Verified nudge pool from the pageview event response (pills rewrite
        // P4). The url guard drops a stale response landing after a fast SPA
        // hop — the new page's own pageview reseeds.
        if (samePagePath(data.url)) {
          setNudgeFloatCount(data.floatCount);
          setNudgeMobileCount(data.mobileFloatCount);
          setNudgePool(data.questions, undefined, data.borrowed ?? []);
          debugLog("[BlueHat] nudge pool seeded:", data.questions.length, "native +", (data.borrowed ?? []).length, "borrowed, floatCount", data.floatCount ?? "(default)");
          pushSectionBarView();
          // Scroll rotation (P6): settling into a new document third deals
          // the next window — the section-scroll trigger this replaces needed
          // sections the server no longer sends.
          nudgeScroll?.stop();
          // Crossing into a new document third deals the next window — the
          // pills change as the visitor moves down the page, which is the
          // whole useful half of the retired scroll-ahead idea, sourced
          // entirely from VERIFIED nudges. onFraction is gone with the
          // section slot it existed to re-target.
          nudgeScroll = startNudgeScroll({
            onThirdChange: () => {
              rotateNudges();
              pushSectionBarView();
            },
          });
        }
        break;
      case "config":
        // Remember it (survives config-before-mount) and apply. Logged so the
        // visibility decision is never invisible again.
        lastChatEnabled = data.chatEnabled;
        debugLog(
          "[BlueHat] config received: chatEnabled =",
          data.chatEnabled,
          "primaryCta =",
          data.primaryCta?.slug ?? null,
          "livingBarSkin =",
          data.livingBarSkin ?? null,
          "— mounted:",
          !!handle,
        );
        applyChatEnabled();
        // Display mode + generated design (placements + theme). Default-safe:
        // display="bubble" + design=null leaves the classic widget unchanged.
        lastDisplay = data.display;
        handle?.setDisplay(data.display);
        // Designed bottom bar (copy + enabled) — anchorless, so it flows to the
        // bubble directly instead of through renderPlacements.
        handle?.setBottomBar(data.design?.placements.bottom_bar ?? null);
        // Panel-header Book CTA (chrome) — null removes the button when disabled.
        handle?.setPrimaryCta?.(data.primaryCta ?? null);
        // Section living-bar skin from Widget Design page.
        // Always apply (including default sticky_notes) so a dashboard switch
        // is not ignored when the field is missing/stale.
        // A/B tests FIRST: a variant overrides the site's normal settings below
        // (skin, what the lower bar shows, the inline default), and it has to
        // be in place before the first paint the config allows.
        abState = resolveAbTests(data.experiments, {
          storage: hostLocalStorage(),
          search: window.location.search,
          fallbackSeed: readHostSession(),
        });
        if (Object.keys(abState.picks).length > 0) debugLog("[BlueHat] A/B tests:", abState.picks);
        lastLivingBarSkin =
          abState.overrides.skin ??
          (isLivingBarSkin(data.livingBarSkin) ? data.livingBarSkin : DEFAULT_LIVING_BAR_SKIN);
        handle?.setLivingBarSkin?.(lastLivingBarSkin);
        // Shared legacy-mapping rule — see @bluehat/shared/voice/mode. No
        // direct push here: applyScope("config") below is the single point
        // that delivers the (page-gated) mode to the bubble.
        lastVoiceMode = resolveVoiceMode(data.voiceMode, data.voiceAssist === true);
        lastVoiceAssist = lastVoiceMode !== "off";
        if (typeof data.ttsVoice === "string" && data.ttsVoice.trim()) {
          setTtsVoice(data.ttsVoice.trim());
        }
        // Independent of ttsVoice: the STT engine choice (incl. the
        // widget_stt_engine="browser" rollback lever) must apply even when a
        // config arrives without a TTS voice — nesting it inside the voice
        // guard silently disabled the kill-switch in that case.
        setSttEngine(data.sttEngine ?? "auto");
        debugLog(
          "[BlueHat] config livingBarSkin =",
          lastLivingBarSkin,
          "voiceAssist =",
          lastVoiceAssist,
          "ttsVoice =",
          data.ttsVoice ?? "nova",
          "— mounted:",
          !!handle,
        );
        if (data.design && customerId) {
          lastDesign = data.design;
          setupPlacementObserver();
        }
        // Scope LAST, and it owns the final word on every surface: it re-pushes
        // voice / bar / banner with the page's verdict applied on top of the
        // values set above, and re-renders placements through the masked
        // design. Theme, typewriter re-bind and the scroll-dock refresh that
        // used to sit here all live in renderPlacementsGuarded, which this
        // calls — the raw renderPlacements that was here bypassed the
        // observer disconnect/re-observe dance and was a trap for the next
        // person to add a call beside it.
        setScopeRules(data.scope);
        // #589: reveal option + the dock's fail-closed latch. Order matters —
        // set the option BEFORE marking config ready, so the ready-triggered
        // evaluate sees the tenant's setting on its first pass.
        lastRevealOnScroll = data.revealOnScroll === true;
        scrollDock?.setRevealOnScroll(lastRevealOnScroll);
        // #592: inline placement mode — free-form on the wire; unknown values
        // (bundle skew) degrade to the default. Mode changes after mount are a
        // cheap state switch through the handle, NEVER a remount (a remount
        // resets chat state).
        lastInlineMode =
          abState.overrides.inlineMode ?? (isInlineMode(data.inlineMode) ? data.inlineMode : DEFAULT_INLINE_MODE);
        // Lower-bar mode — free-form on the wire; an unknown value (bundle
        // skew) degrades to the shipped behaviour rather than hiding the bar.
        lastBarMode = isBarMode(data.barMode) ? data.barMode : DEFAULT_BAR_MODE;
        lastBarContent =
          abState.overrides.barContent ?? (isBarContent(data.barContent) ? data.barContent : DEFAULT_BAR_CONTENT);
        handle?.setBarNudgesOnly?.(lastBarContent === "nudges");
        lastInlineClose = data.inlineClose === true;
        handle?.setInlineCloseAllowed?.(lastInlineClose);
        applyInlineMode();
        lastConfigLanded = true;
        // AFTER the skin + mode setters above: React batches the whole
        // handler into one render, so the surface's first allowed paint
        // already carries the config's look (#592 flash gate).
        handle?.setInlineConfigLanded?.();
        scrollDock?.markConfigReady();
        // Reconciled upstream — a boolean here, never a timestamp comparison.
        setMasterOff(data.terminated === true);
        // Before handleTriggers below could reinstall observers.
        telemetryOff = data.telemetryDisabled === true;
        // "Show on phones" (2026-08-26): absence (older iframe) = true.
        // Before applyScope, which owns delivering the verdict to surfaces.
        setShowOnPhones(data.showOnPhones !== false);
        applyScope("config");
        break;
      case "terminate":
        // Master off, live. A REVERSIBLE all-surfaces-off through the same
        // pathway a rule uses — NOT disposeWidget. Removing the iframe would
        // destroy the only channel that can deliver the un-terminate, and
        // widgetDisposed would kill the SPA hook this very feature relies on.
        // Tracking deliberately keeps running.
        setMasterOff(true);
        applyScope("terminate");
        break;
      case "triggers":
        handleTriggers(data.triggers);
        break;
      case "command":
        executeCommand(data, {
          openChat: () => handle?.open(),
          closeChat: () => handle?.setVisible(false),
          // Present a navigate CTA in the chat: make sure the window is
          // open so the button is visible, then append it.
          presentCta: (url, label, slug, openInNewTab) => {
            handle?.open();
            handle?.addCta(url, label, slug, openInNewTab);
          },
          presentForm: (form) => {
            handle?.open();
            handle?.addForm(form);
          },
          presentChip: (label, sendText) => {
            handle?.open();
            handle?.addChip(label, sendText);
          },
          presentProducts: (products) => {
            // The answer states no prices, so cards arriving without the panel
            // open would hide the only figures the shopper has. Open, like a
            // presented CTA does.
            handle?.addProducts(products);
            handle?.open();
          },
          presentAnswerChips: (chips, turnId, kind, deferred) => {
            // Attach FIRST — the bubble reports whether the chips actually
            // rendered (false = a newer visitor message is in flight, or all
            // options were already asked). Discarded chips must neither force
            // the panel open nor log answer_chips_shown.
            const accepted = handle?.addAnswerChips(chips, turnId, kind) ?? false;
            if (!accepted) return;
            // Deferred chips (minted after their answer already shipped)
            // attach quietly: if the visitor closed the panel during the gap,
            // re-opening it 2s later for footer chrome is rude. Chips that
            // ride the answer frame (rich-lead consent poles) keep the open.
            if (!deferred) handle?.open();
            const chipKind =
              kind === "context_next_ask" ? "context_next_ask" : "answer";
            callbacks.onAnswerChipsShown?.(
              chips.map((c) => c.label),
              turnId,
              chipKind,
            );
          },
        });
        break;
      case "rejected":
        // Domain not allowed — silently remove the widget
        widgetDisposed = true;
        try {
          window.removeEventListener(
            "bhai-conversation-focus",
            onConversationFocusUpdated,
          );
        } catch {
          /* ignore */
        }
        if (observerCleanup) observerCleanup();
        if (adminStyleCleanup) adminStyleCleanup();
        stopTargetObserver();
        stopStickyObserver();
        if (bannerObserver) {
          bannerObserver.disconnect();
          bannerObserver = null;
        }
        if (placementObserver) {
          placementObserver.disconnect();
          placementObserver = null;
        }
        sectionScroll?.stop();
        sectionScroll = null;
        nudgeScroll?.stop();
        nudgeScroll = null;
        clearVoiceNavAnnounceTimer();
        clearRetiredSectionStage();
        scrollDock?.disconnect();
        scrollDock = null;
        iframe.remove();
        if (handle) {
          handle.unmount();
          handle = null;
        }
        return;
    }
  });

  // --- SPA route-change awareness ---
  // The hidden iframe loads once, so the first pageview + trigger setup are
  // anchored to the initial URL. On client-side (History API) navigation the
  // backend would otherwise never learn the visitor moved: server-side pageview
  // triggers wouldn't fire, and client triggers scoped to the new page wouldn't
  // activate. Wrap the History API + popstate, then on each real URL change
  // re-fire a pageview and re-evaluate triggers. (pageview events are analytics,
  // not billed — billing is per chat message.)
  let lastTrackedUrl = window.location.href;
  let routeCheckQueued = false;
  // #595 arrival credit (belt-and-suspenders): every presented CTA URL
  // carries bhai_ctaslug, so the DESTINATION page credits the click even
  // when the pre-navigation POST loses the unload race (or a presentation
  // path dropped the slug). Session-guarded per slug; the server also
  // dedups per (session, slug), so a double credit collapses to one.
  function checkCtaArrival() {
    const slug = extractCtaArrival(window.location.search);
    if (!slug) return;
    const guard = `bhai_cta_arrival_${slug}`;
    try {
      if (sessionStorage.getItem(guard) === "1") return;
      sessionStorage.setItem(guard, "1");
    } catch {
      /* private mode — still credit once per load */
    }
    // A `p_`-prefixed slug is a PRODUCT, not a CTA. It must not be credited as a
    // cta_click: a product id does not resolve against the `ctas` table, and
    // `cta_clicked` feeds "All Conversions" — so crediting it there would inflate a
    // headline conversion number with an unresolvable slug.
    const productId = productIdFromSlug(slug);
    postToIframe(
      productId
        ? {
            type: "event",
            event: "product_click",
            productId,
            ctaSlug: slug,
            url: window.location.href,
            via: "arrival",
          }
        : {
            type: "event",
            event: "cta_click",
            ctaSlug: slug,
            url: window.location.href,
            via: "arrival",
          },
    );
    // Strip the param so copied links and reloads stay clean. Sync the
    // route tracker FIRST so this replaceState never fires a phantom
    // pageview through the patched History API below.
    const cleaned = stripCtaParam(window.location.href);
    if (cleaned !== window.location.href) {
      lastTrackedUrl = cleaned;
      try {
        history.replaceState(history.state, "", cleaned);
      } catch {
        /* ignore */
      }
    }
  }
  checkCtaArrival();
  function handleRouteChange() {
    routeCheckQueued = false;
    if (widgetDisposed) return;
    const url = window.location.href;
    if (url === lastTrackedUrl) return;
    const fromUrl = lastTrackedUrl;
    lastTrackedUrl = url;
    checkCtaArrival();
    // Voice session survives SPA nav: pause mic/speech until new page is ready.
    // Drop any pending scroll-announce so it doesn't fire for the old page.
    clearVoiceNavAnnounceTimer();
    handle?.pauseVoiceForNavigation?.();
    // Re-decide the scope FIRST, before anything renders against it and before
    // the placement re-render further down reads the masked design. Same frame,
    // no round trip — the rules are already here in the parent. The pageview
    // below is deliberately NOT gated: a denied page still reports its visit.
    applyScope("route");
    postToIframe({ type: "event", event: "pageview", url, referrer: fromUrl });
    // handleTriggers tears down the previous observers; setupObservers then
    // re-filters the full trigger set against the new URL.
    if (activeTriggers) handleTriggers(activeTriggers);
    // Drop previous page's corpus chips immediately so SPA nav never shows
    // stale embeddings under the bar while page-voice for the new URL loads.
    lastPageQuestions = [];
    lastPageSections = [];
    // Nudge pool (pills rewrite P4): clear immediately — the new page's own
    // pageview event response reseeds it (nudges:update) and restarts the
    // scroll rotator.
    clearNudgePool();
    nudgeScroll?.stop();
    nudgeScroll = null;
    lastSectionIndex = 0;
    sectionScroll?.stop();
    sectionScroll = null;
    clearRetiredSectionStage();
    lastInviteLines = undefined;
    lastHeroMonologue = undefined;
    lastTextContinuityLead = null;
    // Clear prior page rank — new pageview's server bar re-fills engaged pills.
    // Keep conversation focus so engaged rail mode still applies once bar lands.
    lastRankedPagePills = null;
    lastServerBarKey = "";
    textContinuitySeq += 1;
    // Drop prior page's shared next-asks so Suggested doesn't stick across SPA.
    // Re-seeded by applyServerBar when engaged compose returns for the new URL.
    handle?.setRankedNextAsks?.([]);
    // Drop stale cold-open summary until new page-voice lands.
    handle?.setPageVoiceMeta?.({
      heroMonologue: [],
      inviteLines: [],
      sectionHeadings: [],
    });
    handle?.setRailContinuityLead?.(null);
    // Re-render the placement launchers so their page-aware copy ("Ask me about
    // <topic>") reflects the new page. Also re-assert the observer (idempotent) so
    // anchors the router commits a frame or two late still trigger a render.
    renderPlacementsGuarded();
    setupPlacementObserver();
    handle?.refreshTopic();
    scrollDock?.refresh();
    // Announce when the new page's pageview response lands; the timer only
    // covers a lost pageview POST (announce with empty state, no fetch).
    announceAfterPageVoicePending = true;
    armAnnounceFallback(handle);
  }

  // Coalesce bursts (a router may call replaceState then pushState) into one
  // callback; the URL-equality guard drops no-op changes. Use requestAnimationFrame,
  // NOT a microtask: the router commits the new route's DOM — including the fresh
  // placement anchors — asynchronously, after this task's microtasks. A microtask
  // runs renderPlacements before those anchors exist (so nothing renders and the
  // placements vanish on SPA navigation); rAF runs after the commit.
  function scheduleRouteCheck() {
    if (routeCheckQueued) return;
    routeCheckQueued = true;
    requestAnimationFrame(handleRouteChange);
  }
  const patchHistory = (name: "pushState" | "replaceState") => {
    const original = history[name].bind(history) as History["pushState"];
    history[name] = function (data: unknown, unused: string, url?: string | URL | null) {
      const result = original(data, unused, url);
      scheduleRouteCheck();
      return result;
    };
  };
  patchHistory("pushState");
  patchHistory("replaceState");
  window.addEventListener("popstate", scheduleRouteCheck);
  // Hash routing fires `hashchange`, NOT popstate, and never touches
  // pushState — so on an Angular HashLocationStrategy site (or an older Vue
  // router, or plenty of Shopify/Wix apps) every route looked like the landing
  // URL and no navigation was ever observed. That silently cost those sites
  // per-page placements and pageviews long before scope rules existed; scope
  // just makes it visible, because a rule would appear to do nothing.
  window.addEventListener("hashchange", scheduleRouteCheck);

  // "Show on phones" reacts to the viewport LIVE (rotation, a window dragged
  // across the breakpoint): re-run the scope evaluation through the same seam
  // a route change uses. With the setting on (the default) the evaluation is
  // a no-op, so this listener costs nothing for tenants who never touch it.
  try {
    window
      .matchMedia(`(max-width: ${PHONE_BREAKPOINT_PX}px)`)
      .addEventListener("change", () => applyScope("viewport"));
  } catch {
    /* very old engines: the setting still applies, evaluated at load only */
  }

  // Execute any pending post-navigation actions from the action stack
  drainActionStack();
})();
